// src/components/assets/VerifyAssetModal.tsx
// Modal for performing annual asset verification
// Created: 2025-02-01

import React, { useState } from 'react';
import { X, CheckCircle, MapPin, AlertTriangle } from 'lucide-react';
import { TrackedAsset, AssetCondition, CONDITION_LABELS } from '../../types/asset';
import { assetsAPI } from '../../services/assetsAPI';

interface VerifyAssetModalProps {
  asset: TrackedAsset;
  isOpen: boolean;
  onClose: () => void;
  onVerified: (updatedAsset: TrackedAsset) => void;
}

const VerifyAssetModal: React.FC<VerifyAssetModalProps> = ({
  asset,
  isOpen,
  onClose,
  onVerified
}) => {
  const [locationVerified, setLocationVerified] = useState(asset.location || '');
  const [condition, setCondition] = useState<AssetCondition>('good');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const locationChanged = locationVerified.trim().toLowerCase() !== (asset.location || '').toLowerCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!locationVerified.trim()) {
      setError('Please confirm the asset location');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await assetsAPI.verifyAsset(asset.assetId, {
        locationVerified: locationVerified.trim(),
        condition,
        notes: notes.trim() || undefined
      });

      if (response.success && response.data) {
        onVerified(response.data.asset);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to verify asset');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseCurrentLocation = () => {
    setLocationVerified(asset.location || '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-green-50">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Verify Asset</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Asset Info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900">{asset.name}</p>
              <p className="text-sm text-gray-500">{asset.assetTag}</p>
            </div>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
              {asset.categoryName}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-1 text-sm text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>Expected: {asset.location || 'Not specified'}</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Location Verification */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verified Location <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={locationVerified}
                onChange={(e) => setLocationVerified(e.target.value)}
                placeholder="Enter the actual location of the asset"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
              {locationVerified !== asset.location && (
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  title="Use expected location"
                >
                  Reset
                </button>
              )}
            </div>
            {locationChanged && (
              <div className="mt-2 flex items-center gap-1 text-sm text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                <span>Location differs from expected. Asset location will be updated.</span>
              </div>
            )}
          </div>

          {/* Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condition
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(Object.keys(CONDITION_LABELS) as AssetCondition[]).map((cond) => (
                <button
                  key={cond}
                  type="button"
                  onClick={() => setCondition(cond)}
                  className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    condition === cond
                      ? cond === 'good'
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : cond === 'fair'
                        ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : cond === 'poor'
                        ? 'bg-orange-100 border-orange-500 text-orange-700'
                        : 'bg-red-100 border-red-500 text-red-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {CONDITION_LABELS[cond]}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this verification..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Confirm Verification
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyAssetModal;
